import React, { useEffect, useRef, useState } from 'react';
import { postsRedes } from '../data/contenido';
import { contacto } from '../data/viajes';
import useRecuadroTikTok from '../hooks/useRecuadroTikTok';
import { cargarPostsInstagram, TIKTOK_USUARIO } from '../services/redes';
import { mini } from '../utils';
import './Redes.css';

// Recuadro oficial del perfil de TikTok: foto, seguidores y sus videos más recientes (lo arma el script de TikTok).
// Ocupa todo el ancho de su tarjeta, como la cuadrícula de Instagram (el código de TikTok trae 780 px como máximo).
// Mientras el script no lo convierte, adentro queda el enlace al perfil.
const EMBED_TIKTOK = `<blockquote class="tiktok-embed" cite="https://www.tiktok.com/@${TIKTOK_USUARIO}" data-unique-id="${TIKTOK_USUARIO}" data-embed-type="creator" style="max-width:100%;min-width:288px"><section><a target="_blank" rel="noopener noreferrer" href="https://www.tiktok.com/@${TIKTOK_USUARIO}?refer=creator_embed">@${TIKTOK_USUARIO} en TikTok</a></section></blockquote>`;
/** TikTok se empieza a cargar cuando a la sección le falta menos de pantalla y media para aparecer. */
const MARGEN_TIKTOK = '150% 0px';

/** @type {{ id: 'ig'|'tt', nombre: string, icono: string }[]} */
const PESTANAS = [
  { id: 'ig', nombre: 'Instagram', icono: 'fa-brands fa-instagram' },
  { id: 'tt', nombre: 'TikTok', icono: 'fa-brands fa-tiktok' }
];
/** Icono de cada tipo de publicación de Instagram. */
const ICONO_TIPO = { foto: 'fa-brands fa-instagram', video: 'fa-solid fa-play', carrusel: 'fa-regular fa-clone' };

export default function Redes() {
  const [pestana, setPestana] = useState('ig');
  // TikTok se pide antes de abrir su pestaña (cuando la sección se acerca) para que al abrirla ya esté listo;
  // después se queda (no se recarga al cambiar de pestaña)
  const [tiktokPedido, setTiktokPedido] = useState(false);
  const [posts, setPosts] = useState(/** @type {import('../services/redes').PostInstagram[]} */ ([]));
  const botones = useRef({});
  const seccion = useRef(null);
  const recuadro = useRef(null);
  const tiktok = useRecuadroTikTok(recuadro, tiktokPedido, pestana === 'tt');

  useEffect(() => {
    let vivo = true;
    cargarPostsInstagram().then((p) => { if (vivo) setPosts(p); });
    return () => { vivo = false; };
  }, []);
  useEffect(() => {
    const el = seccion.current;
    if (tiktokPedido || !el) return undefined;
    if (typeof IntersectionObserver !== 'function') { setTiktokPedido(true); return undefined; }
    const vista = new IntersectionObserver((entradas) => {
      if (entradas.some((x) => x.isIntersecting)) setTiktokPedido(true);
    }, { rootMargin: MARGEN_TIKTOK });
    vista.observe(el);
    return () => vista.disconnect();
  }, [tiktokPedido]);

  const elegir = (id, enfocar) => {
    setPestana(id);
    if (id === 'tt') setTiktokPedido(true);
    if (enfocar && botones.current[id]) botones.current[id].focus();
  };
  // con teclado, las flechas izquierda y derecha pasan de una pestaña a otra
  const alTeclear = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = PESTANAS.findIndex((p) => p.id === pestana), n = PESTANAS.length;
    elegir(PESTANAS[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n].id, true);
  };

  return (
    <section id="redes" className="redes sec" ref={seccion}>
      <div className="wrap">
        <div className="redes__head" data-reveal>
          <div className="redes__intro">
            <p className="eyebrow">Viaja conmigo</p>
            <h2 className="h2">Lo que vivo, <em>lo cuento en redes</em></h2>
            <p className="lead">Comparto mis viajes, tips y novedades de los parques. Sígueme y empieza a viajar conmigo antes de tu propio viaje.</p>
          </div>
          <div className="redes__btns">
            <a className="btn-dark" href={contacto.instagram} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-instagram" />@experienciasbyjenny</a>
            <a className="redes__tt" href={contacto.tiktok} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-tiktok" />@{TIKTOK_USUARIO}</a>
          </div>
        </div>

        <div className="redes__tabs" role="tablist" aria-label="Mis redes" onKeyDown={alTeclear}>
          {PESTANAS.map((p) => (
            <button key={p.id} ref={(b) => { botones.current[p.id] = b; }} type="button" role="tab" id={`redes-tab-${p.id}`}
              aria-controls={`redes-panel-${p.id}`} aria-selected={pestana === p.id} tabIndex={pestana === p.id ? 0 : -1}
              className={pestana === p.id ? 'is-on' : ''} onClick={() => elegir(p.id)}>
              <i className={p.icono} />{p.nombre}
            </button>
          ))}
        </div>

        <div className="redes__paneles">
          <div className="redes__panel" role="tabpanel" id="redes-panel-ig" aria-labelledby="redes-tab-ig" hidden={pestana !== 'ig'}>
            {posts.length ? (
              <>
                <div className="redes__grid redes__grid--ig">
                  {posts.map((p) => (
                    <a key={p.id} className="redes__post redes__post--ig" href={p.url} target="_blank" rel="noopener noreferrer">
                      <img src={p.imagen} alt={p.texto || 'Publicación de Instagram'} loading="lazy" decoding="async" />
                      <span><i className={ICONO_TIPO[p.tipo]} /><em>{p.texto || 'Ver en Instagram'}</em></span>
                    </a>
                  ))}
                </div>
                <a className="redes__mas" href={contacto.instagram} target="_blank" rel="noopener noreferrer">Ver más en Instagram <i className="fa-solid fa-arrow-right" /></a>
              </>
            ) : (
              // sin feed de Instagram (o mientras carga): las fotos elegidas a mano
              <div className="redes__grid">
                {postsRedes.map(({ foto, lugar, red }) => (
                  <a key={foto} className="redes__post" href={red === 'ig' ? contacto.instagram : contacto.tiktok} target="_blank" rel="noopener noreferrer" data-reveal>
                    <img src={mini(`images/${foto}`)} alt={lugar} loading="lazy" decoding="async" />
                    <span><i className={red === 'ig' ? 'fa-brands fa-instagram' : 'fa-brands fa-tiktok'} />{lugar}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* con la pestaña de Instagram abierta, TikTok se arma detrás: invisible y sin ocupar lugar */}
          <div className={`redes__panel${pestana === 'tt' ? '' : ' redes__panel--fondo'}`} role="tabpanel" id="redes-panel-tt" aria-labelledby="redes-tab-tt">
            {tiktokPedido && (
              // tarjeta propia alrededor del recuadro de TikTok: el encabezado es del sitio y el interior no se toca
              <div className="redes__tt-marco">
                <div className="redes__tt-cabeza">
                  <span className="redes__tt-logo" aria-hidden="true"><i className="fa-brands fa-tiktok" /></span>
                  <div className="redes__tt-titulo">
                    <p className="eyebrow">En TikTok</p>
                    <h3>Mis videos <em>más recientes</em></h3>
                  </div>
                  <a className="btn-dark redes__tt-seguir" href={contacto.tiktok} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-tiktok" />Sígueme en TikTok</a>
                </div>
                <div className="redes__tt-cuerpo" data-estado={tiktok.estado}>
                  <div className="redes__tiktok" ref={recuadro} dangerouslySetInnerHTML={{ __html: EMBED_TIKTOK }} />
                  {/* mientras TikTok no responde, un aviso tapa su iframe (que sigue debajo para que TikTok lo termine) */}
                  {tiktok.estado !== 'listo' && (
                    <div className="redes__tt-aviso" role="status">
                      {tiktok.estado === 'cargando' ? (
                        <>
                          <span className="redes__tt-giro" aria-hidden="true" />
                          <p>Cargando mis videos de TikTok…</p>
                        </>
                      ) : (
                        <>
                          <p className="redes__tt-aviso-titulo">TikTok no cargó esta vez</p>
                          <p>A veces TikTok tarda o no responde. Mis videos siempre están en mi perfil.</p>
                          <div className="redes__tt-aviso-btns">
                            <a className="btn-dark" href={contacto.tiktok} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-tiktok" />Ver mis videos en TikTok</a>
                            <button type="button" className="redes__tt-reintentar" onClick={tiktok.reintentar}><i className="fa-solid fa-rotate-right" />Reintentar</button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
